<template>
  <div
    class="chat-panel-root"
    style="display: flex; flex-direction: column; height: 100%; overflow: hidden; background: white;"
  >
    <!-- Title bar -->
    <div
      class="primary d-flex align-center pl-3 py-1"
      style="flex-shrink: 0; font-size: 1rem; font-weight: 500;"
    >
      <v-icon color="white" large class="mr-2">mdi-chat-outline</v-icon>
      <span class="white--text">Team Chat</span>
    </div>

    <!-- Header -->
    <div
      class="chat-panel-header d-flex align-center justify-space-between px-2 py-1"
      style="border-bottom: 1px solid #e0e0e0; flex-shrink: 0;"
    >
      <collection-switcher-menu
        :active-collection="activeCollection"
        :available-collections="availableCollections"
        style="max-width: 60%; min-width: 0;"
        @pick="onPickCollection"
        @clear="onClearCollection"
      />
      <div style="flex-shrink: 0;">
        <v-btn icon small title="Expand to fullscreen" @click="openFullscreen">
          <v-icon small>mdi-arrow-expand</v-icon>
        </v-btn>
        <v-btn icon small @click="closePanel">
          <v-icon small>close</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Empty state (no active collection) -->
    <chat-empty-state
      v-if="!activeCollection"
      compact
      @take-tour="startTour"
    ></chat-empty-state>

    <!-- Chat UI (collection selected) -->
    <template v-else>
      <div style="display: flex; flex: 1; overflow: hidden;">
        <!-- Channel sidebar -->
        <div
          style="width: 160px; min-width: 160px; border-right: 1px solid #e0e0e0; display: flex; flex-direction: column; background: #f5f5f5;"
        >
          <div style="flex: 1; overflow-y: auto;">
            <!-- Channels -->
            <v-tooltip right color="primary">
              <template v-slot:activator="{ on }">
                <div
                  class="section-header"
                  v-on="on"
                  @click="sectionOpen.channels = !sectionOpen.channels"
                >
                  <span>Channels</span>
                  <v-icon x-small>{{
                    sectionOpen.channels
                      ? 'mdi-chevron-down'
                      : 'mdi-chevron-right'
                  }}</v-icon>
                </div>
              </template>
              <span>General channel and custom topic channels</span>
            </v-tooltip>
            <v-list v-if="sectionOpen.channels" dense class="py-0">
              <v-tooltip
                v-for="ch in generalAndOtherChannels"
                :key="ch.name"
                right
                color="primary"
              >
                <template v-slot:activator="{ on }">
                  <v-list-item
                    :class="
                      activeChannel === ch.name ? 'primary lighten-5' : ''
                    "
                    v-on="on"
                    @click="selectChannel(ch)"
                  >
                    <v-list-item-content>
                      <v-list-item-title
                        :class="channelTitleClass(ch)"
                        style="font-size: 0.8rem;"
                      >
                        {{ channelDisplayName(ch) }}
                      </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action
                      v-if="ch.type === 'other'"
                      class="my-0 ml-0"
                      style="min-width: 0;"
                    >
                      <v-btn
                        icon
                        x-small
                        title="Leave channel"
                        class="leave-btn"
                        @click.stop="confirmLeave(ch)"
                      >
                        <v-icon x-small>close</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </template>
                <span>{{ channelDisplayName(ch) }}</span>
              </v-tooltip>
            </v-list>

            <!-- Cohorts -->
            <template v-if="cohortChannels.length > 0">
              <v-divider></v-divider>
              <v-tooltip right color="primary">
                <template v-slot:activator="{ on }">
                  <div
                    class="section-header"
                    v-on="on"
                    @click="sectionOpen.cohorts = !sectionOpen.cohorts"
                  >
                    <span>Study groups</span>
                    <v-icon x-small>{{
                      sectionOpen.cohorts
                        ? 'mdi-chevron-down'
                        : 'mdi-chevron-right'
                    }}</v-icon>
                  </div>
                </template>
                <span>Channels linked to your study cohorts</span>
              </v-tooltip>
              <v-list v-if="sectionOpen.cohorts" dense class="py-0">
                <v-tooltip
                  v-for="ch in cohortChannels"
                  :key="ch.name"
                  right
                  color="primary"
                >
                  <template v-slot:activator="{ on }">
                    <v-list-item
                      :class="
                        activeChannel === ch.name ? 'primary lighten-5' : ''
                      "
                      v-on="on"
                      @click="selectChannel(ch)"
                    >
                      <v-list-item-content>
                        <v-list-item-title
                          :class="channelTitleClass(ch)"
                          style="font-size: 0.8rem;"
                        >
                          {{ channelDisplayName(ch) }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                  <span>{{ channelDisplayName(ch) }}</span>
                </v-tooltip>
              </v-list>
            </template>

            <!-- Analyses -->
            <template v-if="analysisChannels.length > 0">
              <v-divider></v-divider>
              <v-tooltip right color="primary">
                <template v-slot:activator="{ on }">
                  <div
                    class="section-header"
                    v-on="on"
                    @click="sectionOpen.analyses = !sectionOpen.analyses"
                  >
                    <span>Analyses</span>
                    <v-icon x-small>{{
                      sectionOpen.analyses
                        ? 'mdi-chevron-down'
                        : 'mdi-chevron-right'
                    }}</v-icon>
                  </div>
                </template>
                <span>Channels linked to your saved analyses</span>
              </v-tooltip>
              <v-list v-if="sectionOpen.analyses" dense class="py-0">
                <v-tooltip
                  v-for="ch in analysisChannels"
                  :key="ch.name"
                  right
                  color="primary"
                >
                  <template v-slot:activator="{ on }">
                    <v-list-item
                      :class="
                        activeChannel === ch.name ? 'primary lighten-5' : ''
                      "
                      v-on="on"
                      @click="selectChannel(ch)"
                    >
                      <v-list-item-content>
                        <v-list-item-title
                          :class="channelTitleClass(ch)"
                          style="font-size: 0.8rem;"
                        >
                          {{ channelDisplayName(ch) }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                  <span>{{ channelDisplayName(ch) }}</span>
                </v-tooltip>
              </v-list>
            </template>

            <!-- DMs -->
            <v-divider></v-divider>
            <div
              class="section-header"
              @click="sectionOpen.dms = !sectionOpen.dms"
            >
              <v-tooltip right color="primary">
                <template v-slot:activator="{ on }">
                  <span style="flex: 1;" v-on="on">Direct Messages</span>
                </template>
                <span>Private one-on-one and group conversations</span>
              </v-tooltip>
              <div class="d-flex align-center">
                <v-tooltip right color="primary">
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      x-small
                      style="margin-right: 2px;"
                      v-on="on"
                      @click.stop="startNewConversation"
                    >
                      <v-icon x-small>mdi-plus</v-icon>
                    </v-btn>
                  </template>
                  <span>Create new group direct message</span>
                </v-tooltip>
                <v-icon x-small>{{
                  sectionOpen.dms ? 'mdi-chevron-down' : 'mdi-chevron-right'
                }}</v-icon>
              </div>
            </div>
            <v-list v-if="sectionOpen.dms" dense class="py-0">
              <v-tooltip
                v-for="ch in dmChannels"
                :key="ch.name"
                right
                :color="revokedHoverChannel === ch.name ? 'warning' : 'primary'"
              >
                <template v-slot:activator="{ on }">
                  <v-list-item
                    :class="
                      activeChannel === ch.name ? 'primary lighten-5' : ''
                    "
                    v-on="on"
                    @click="selectChannel(ch)"
                  >
                    <v-list-item-content>
                      <v-list-item-title
                        :class="channelTitleClass(ch)"
                        style="font-size: 0.8rem;"
                      >
                        {{ channelDisplayName(ch) }}
                      </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action
                      v-if="dmHasRevokedMember(ch)"
                      class="my-0 ml-1"
                      style="min-width: 0;"
                      @mouseenter="revokedHoverChannel = ch.name"
                      @mouseleave="revokedHoverChannel = null"
                    >
                      <v-icon x-small color="warning">mdi-account-off</v-icon>
                    </v-list-item-action>
                  </v-list-item>
                </template>
                <span v-if="revokedHoverChannel === ch.name"
                  >One or more participants no longer have access to this
                  dataset</span
                >
                <span v-else>{{ channelDisplayName(ch) }}</span>
              </v-tooltip>
            </v-list>
          </div>

          <!-- Add channel -->
          <div
            class="pa-2"
            style="border-top: 1px solid #e0e0e0; flex-shrink: 0;"
          >
            <v-btn x-small text color="primary" @click="openNewChannelDialog">
              <v-icon x-small left>add</v-icon>New Channel
            </v-btn>
          </div>
        </div>

        <!-- Message thread / new conversation composer -->
        <div
          style="flex: 1; display: flex; flex-direction: column; overflow: hidden; min-width: 0;"
        >
          <!-- New conversation composer -->
          <template v-if="newConversationMode">
            <div
              class="px-2 py-1 d-flex align-center"
              style="border-bottom: 1px solid #e0e0e0; flex-shrink: 0;"
            >
              <span class="caption font-weight-bold primary--text"
                >New Conversation</span
              >
              <v-spacer></v-spacer>
              <v-btn icon x-small @click="cancelNewConversation">
                <v-icon x-small>close</v-icon>
              </v-btn>
            </div>
            <div class="pa-3" style="flex: 1; overflow-y: auto;">
              <div class="caption grey--text mb-2">
                Select one person for a direct message, or multiple people for a
                group conversation.
              </div>
              <v-autocomplete
                v-model="newConvSelectedUsers"
                :items="collectionMembers"
                :search-input.sync="newConvSearch"
                item-text="name"
                item-value="id"
                label="To:"
                dense
                outlined
                multiple
                chips
                small-chips
                deletable-chips
                hide-selected
                hide-details
                no-data-text="No items"
                class="mb-3"
                style="font-size: 0.85em;"
                autofocus
                @change="newConvSearch = ''"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
              <v-btn
                small
                color="primary"
                :disabled="newConvSelectedUsers.length === 0"
                @click="openNewConversation"
              >
                Open
              </v-btn>
              <v-btn small text class="ml-1" @click="cancelNewConversation"
                >Cancel</v-btn
              >
            </div>
          </template>

          <!-- Normal channel view -->
          <template v-else>
            <!-- Channel header -->
            <div
              class="px-2 py-1 d-flex align-center"
              style="border-bottom: 1px solid #e0e0e0; flex-shrink: 0;"
            >
              <v-menu
                offset-y
                :close-on-content-click="false"
                max-height="300"
                min-width="200"
              >
                <template v-slot:activator="{ on, attrs }">
                  <span
                    class="caption font-weight-bold primary--text text-truncate channel-header-name"
                    v-bind="attrs"
                    style="cursor: pointer;"
                    v-on="on"
                  >
                    {{ activeChannelHeaderLabel }}
                    <v-icon x-small color="primary" class="ml-1"
                      >mdi-chevron-down</v-icon
                    >
                  </span>
                </template>
                <v-card>
                  <v-card-subtitle class="pb-1 pt-2 caption font-weight-bold">
                    Members ({{ activeChannelMembers.length }})
                  </v-card-subtitle>
                  <v-list dense class="pt-0">
                    <v-list-item
                      v-for="member in activeChannelMembers"
                      :key="member.user_id"
                      class="py-0"
                      style="min-height: 32px;"
                    >
                      <v-list-item-icon class="mr-2 my-1">
                        <v-icon small>mdi-account</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="caption">
                          {{ member.name }}
                          <span
                            v-if="
                              currentUser && member.user_id === currentUser.id
                            "
                            class="grey--text"
                            >(you)</span
                          >
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-menu>
              <v-spacer></v-spacer>
              <span
                v-if="channelEntityTag"
                class="msg-tag msg-tag--mention channel-entity-tag"
                :class="
                  channelEntityTag.deleted
                    ? 'msg-tag--deleted'
                    : 'primary--text'
                "
                @click="onTagClick(channelEntityTag)"
                >{{ channelEntityTag.display
                }}<v-icon
                  x-small
                  :color="channelEntityTag.deleted ? '' : 'primary'"
                  class="msg-tag-icon"
                  >{{ channelEntityTag.icon }}</v-icon
                ></span
              >
              <v-spacer></v-spacer>
              <v-btn
                v-if="activeChannelObj && canAddPeople(activeChannelObj)"
                x-small
                text
                color="primary"
                style="flex-shrink: 0;"
                @click="openAddPeople"
              >
                <v-icon x-small left>mdi-account-plus</v-icon>Add people
              </v-btn>
              <span v-else class="caption grey--text"
                >{{ messages.length }} message(s)</span
              >
            </div>

            <!-- Messages -->
            <div
              ref="messageList"
              style="flex: 1; overflow-y: auto; background: #fafafa; padding: 8px 0;"
            >
              <v-container v-if="isLoading" fluid>
                <v-row justify="center"
                  ><v-progress-circular
                    indeterminate
                    color="primary"
                    size="24"
                  ></v-progress-circular
                ></v-row>
              </v-container>
              <div
                v-else-if="!activeChannel"
                class="text-center grey--text mt-4 caption"
              >
                Select a channel.
              </div>
              <div
                v-else-if="messages.length === 0"
                class="text-center grey--text mt-4 caption"
              >
                No messages yet.
              </div>
              <template v-for="(msg, index) in messages">
                <div
                  v-if="isNewDay(msg, index)"
                  :key="'day-' + msg.id"
                  class="day-banner"
                >
                  <span class="day-banner-label">{{
                    formatDayLabel(msg.date_generated)
                  }}</span>
                </div>
                <div
                  :key="msg.id"
                  :data-message-id="msg.id"
                  class="message-row"
                  :class="{
                    'message-block-start': !isContinuation(msg, index),
                  }"
                >
                  <div v-if="!isContinuation(msg, index)" class="msg-avatar">
                    {{ userInitial(msg.user) }}
                  </div>
                  <div class="msg-body">
                    <span
                      v-if="isContinuation(msg, index)"
                      class="msg-continuation-time caption grey--text"
                      >{{ formatTimeShort(msg.date_generated) }}</span
                    >
                    <div
                      v-if="!isContinuation(msg, index)"
                      class="msg-meta d-flex align-center"
                    >
                      <span
                        class="font-weight-bold"
                        style="font-size: 0.85em;"
                        >{{ msg.user }}</span
                      >
                      <span class="caption grey--text ml-2">{{
                        formatTime(msg.date_generated)
                      }}</span>
                      <span
                        v-if="msg.date_generated !== msg.last_modified"
                        class="caption grey--text ml-1"
                        >(edited)</span
                      >
                      <div class="msg-actions ml-auto">
                        <v-icon
                          v-if="msg.user_id === currentUser.id"
                          x-small
                          class="mr-1"
                          @click.stop="startEdit(msg)"
                          >edit</v-icon
                        >
                        <v-icon
                          v-if="
                            msg.user_id === currentUser.id || isCollectionOwner
                          "
                          x-small
                          @click.stop="deleteMessage(msg)"
                          >delete</v-icon
                        >
                      </div>
                    </div>
                    <div
                      v-if="editingMessageId === msg.id"
                      style="position: relative;"
                    >
                      <div
                        v-if="editMention.active && editMentionItems.length"
                        class="mention-dropdown"
                      >
                        <v-list dense class="py-0">
                          <v-list-item
                            v-for="(item, i) in editMentionItems"
                            :key="item.token"
                            class="px-2"
                            :class="{
                              'mention-item-active':
                                i === editMentionHighlightIndex,
                            }"
                            @click="selectEditMentionItem(item)"
                            @mouseenter="editMentionHighlightIndex = i"
                          >
                            <v-list-item-icon class="mr-2 my-auto"
                              ><v-icon small>{{
                                item.icon
                              }}</v-icon></v-list-item-icon
                            >
                            <v-list-item-content>
                              <v-list-item-title class="caption"
                                >{{ editMention.sigil
                                }}{{ item.display }}</v-list-item-title
                              >
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </div>
                      <v-textarea
                        ref="editInputDocked"
                        v-model="editingContent"
                        dense
                        filled
                        background-color="#f5f5f5"
                        rows="2"
                        :maxlength="maxLength"
                        hide-details
                        @input.native="onEditInput"
                        @keydown.enter.exact.prevent="handleEditEnterKey"
                        @keydown.native="onEditMentionNavKeydown"
                      ></v-textarea>
                      <div class="d-flex mt-1">
                        <v-btn
                          x-small
                          color="primary"
                          class="mr-1"
                          :disabled="!editingContent.trim()"
                          @click="submitEdit"
                          >Save</v-btn
                        >
                        <v-btn x-small text @click="cancelEdit">Cancel</v-btn>
                      </div>
                    </div>
                    <!-- no whitespace inside: pre-wrap would render template indent as spaces -->
                    <!-- prettier-ignore -->
                    <div v-else style="font-size: 0.85em; white-space: pre-wrap;"><template v-for="(seg, si) in parseContent(msg.content)"><span v-if="seg.type === 'tag'" :key="si" :class="['msg-tag', seg.kind === 'channel' ? 'msg-tag--channel' : 'msg-tag--mention', { 'primary--text': !seg.deleted, 'msg-tag--deleted': seg.deleted }]" @click.stop="onTagClick(seg)">{{ seg.display }}<v-icon x-small :color="seg.deleted ? '' : 'primary'" class="msg-tag-icon">{{ seg.icon }}</v-icon></span><span v-else :key="si">{{ seg.text }}</span></template><span v-if="isContinuation(msg, index) && msg.date_generated !== msg.last_modified" class="caption grey--text ml-1">(edited)</span></div>
                    <div
                      v-if="msg.attachments && msg.attachments.length"
                      class="mt-1"
                    >
                      <img
                        v-for="att in msg.attachments"
                        :key="att.id"
                        :src="att.url"
                        :alt="att.filename"
                        class="msg-attachment-img"
                        @click.stop="openImageDialog(att.url)"
                      />
                    </div>
                  </div>
                  <div
                    v-if="isContinuation(msg, index)"
                    class="msg-actions msg-actions-cont"
                  >
                    <v-icon
                      v-if="msg.user_id === currentUser.id"
                      x-small
                      class="mr-1"
                      @click.stop="startEdit(msg)"
                      >edit</v-icon
                    >
                    <v-icon
                      v-if="msg.user_id === currentUser.id || isCollectionOwner"
                      x-small
                      @click.stop="deleteMessage(msg)"
                      >delete</v-icon
                    >
                  </div>
                </div>
              </template>
            </div>

            <!-- Input -->
            <div
              class="pa-2"
              style="border-top: 1px solid #e0e0e0; flex-shrink: 0; position: relative;"
              @dragover.prevent
              @drop.prevent="onDrop"
            >
              <!-- Mention / channel autocomplete dropdown -->
              <div
                v-if="mention.active && mentionItems.length"
                class="mention-dropdown"
              >
                <v-list dense class="py-0">
                  <v-list-item
                    v-for="(item, i) in mentionItems"
                    :key="item.token"
                    class="px-2"
                    :class="{
                      'mention-item-active': i === mentionHighlightIndex,
                    }"
                    @click="selectMentionItem(item)"
                    @mouseenter="mentionHighlightIndex = i"
                  >
                    <v-list-item-icon class="mr-2 my-auto"
                      ><v-icon small>{{ item.icon }}</v-icon></v-list-item-icon
                    >
                    <v-list-item-content>
                      <v-list-item-title class="caption"
                        >{{ mention.sigil
                        }}{{ item.display }}</v-list-item-title
                      >
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>
              <!-- Pending image preview -->
              <div
                v-if="pendingImagePreview"
                class="d-flex align-center pa-1 mb-1"
                style="background: #f5f5f5; border-radius: 4px;"
              >
                <img
                  :src="pendingImagePreview"
                  style="max-height: 80px; max-width: 120px; border-radius: 4px; object-fit: cover;"
                />
                <span
                  class="caption grey--text ml-2"
                  style="flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
                  >{{ pendingImage ? pendingImage.name : '' }}</span
                >
                <v-btn icon x-small @click="clearPendingImage">
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </div>
              <v-textarea
                ref="messageInput"
                v-model="newMessageContent"
                :placeholder="
                  activeChannel
                    ? `Message ${activeChannelHeaderLabel}`
                    : 'Select a channel first'
                "
                :disabled="!activeChannel"
                dense
                outlined
                rows="2"
                :maxlength="maxLength"
                hide-details
                @input.native="onMessageInput"
                @keydown.enter.exact.prevent="handleEnterKey"
                @keydown.native="onMentionNavKeydown"
                @paste.native="onPaste"
              ></v-textarea>
              <div class="d-flex justify-end mt-1 align-center">
                <span class="caption grey--text mr-2"
                  >{{ newMessageContent.length }}/{{ maxLength }} · Enter
                  ↵</span
                >
                <input
                  ref="imageFileInput"
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  style="display: none;"
                  @change="onFileSelected"
                />
                <v-btn
                  icon
                  x-small
                  class="mr-1"
                  title="Attach image"
                  :disabled="!activeChannel"
                  @click="$refs.imageFileInput.click()"
                >
                  <v-icon small>mdi-paperclip</v-icon>
                </v-btn>
                <v-btn
                  x-small
                  color="primary"
                  :disabled="
                    (!newMessageContent.trim() && !pendingImage) ||
                      !activeChannel
                  "
                  @click="sendMessage"
                  >Send</v-btn
                >
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>

    <!-- Fullscreen dialog -->
    <v-dialog
      v-model="fullscreen"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card style="display: flex; flex-direction: column; height: 100%;">
        <div
          class="primary d-flex align-center justify-space-between px-2"
          style="height: 40px; flex-shrink: 0;"
        >
          <!-- Left: icon + collection switcher -->
          <div
            class="d-flex align-center"
            style="flex: 1; min-width: 0; overflow: hidden;"
          >
            <v-icon color="white" small class="mr-2" style="flex-shrink: 0;"
              >mdi-chat-outline</v-icon
            >
            <collection-switcher-menu
              :active-collection="activeCollection"
              :available-collections="availableCollections"
              dark
              style="min-width: 0; overflow: hidden;"
              @pick="onPickCollection"
              @clear="onClearCollection"
            />
          </div>
          <!-- Right: action buttons -->
          <div style="flex-shrink: 0;">
            <v-btn icon dark small @click="fullscreen = false">
              <v-icon small>mdi-arrow-collapse</v-icon>
            </v-btn>
            <v-btn
              icon
              dark
              small
              @click="
                fullscreen = false;
                closePanel();
              "
            >
              <v-icon small>close</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Fullscreen empty state -->
        <chat-empty-state
          v-if="!activeCollection"
          @take-tour="startTour"
        ></chat-empty-state>

        <div v-else style="display: flex; flex: 1; overflow: hidden;">
          <!-- Fullscreen channel sidebar (wider) -->
          <div
            style="width: 220px; border-right: 1px solid #e0e0e0; display: flex; flex-direction: column; background: #f5f5f5;"
          >
            <div style="flex: 1; overflow-y: auto;">
              <v-tooltip right color="primary">
                <template v-slot:activator="{ on }">
                  <div
                    class="section-header"
                    v-on="on"
                    @click="sectionOpen.channels = !sectionOpen.channels"
                  >
                    <span>Channels</span>
                    <v-icon x-small>{{
                      sectionOpen.channels
                        ? 'mdi-chevron-down'
                        : 'mdi-chevron-right'
                    }}</v-icon>
                  </div>
                </template>
                <span>General channel and custom topic channels</span>
              </v-tooltip>
              <v-list v-if="sectionOpen.channels" dense class="py-0">
                <v-tooltip
                  v-for="ch in generalAndOtherChannels"
                  :key="'fs-' + ch.name"
                  right
                  color="primary"
                >
                  <template v-slot:activator="{ on }">
                    <v-list-item
                      :class="
                        activeChannel === ch.name ? 'primary lighten-5' : ''
                      "
                      v-on="on"
                      @click="selectChannel(ch)"
                    >
                      <v-list-item-content
                        ><v-list-item-title :class="channelTitleClass(ch)">{{
                          channelDisplayName(ch)
                        }}</v-list-item-title></v-list-item-content
                      >
                      <v-list-item-action
                        v-if="ch.type === 'other'"
                        class="my-0 ml-0"
                        style="min-width: 0;"
                      >
                        <v-btn
                          icon
                          x-small
                          title="Leave channel"
                          class="leave-btn"
                          @click.stop="confirmLeave(ch)"
                        >
                          <v-icon x-small>close</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </template>
                  <span>{{ channelDisplayName(ch) }}</span>
                </v-tooltip>
              </v-list>
              <template v-if="cohortChannels.length > 0">
                <v-divider></v-divider>
                <v-tooltip right color="primary">
                  <template v-slot:activator="{ on }">
                    <div
                      class="section-header"
                      v-on="on"
                      @click="sectionOpen.cohorts = !sectionOpen.cohorts"
                    >
                      <span>Study groups</span>
                      <v-icon x-small>{{
                        sectionOpen.cohorts
                          ? 'mdi-chevron-down'
                          : 'mdi-chevron-right'
                      }}</v-icon>
                    </div>
                  </template>
                  <span>Channels linked to your study cohorts</span>
                </v-tooltip>
                <v-list v-if="sectionOpen.cohorts" dense class="py-0">
                  <v-tooltip
                    v-for="ch in cohortChannels"
                    :key="'fs-' + ch.name"
                    right
                    color="primary"
                  >
                    <template v-slot:activator="{ on }">
                      <v-list-item
                        :class="
                          activeChannel === ch.name ? 'primary lighten-5' : ''
                        "
                        v-on="on"
                        @click="selectChannel(ch)"
                      >
                        <v-list-item-content
                          ><v-list-item-title :class="channelTitleClass(ch)">{{
                            channelDisplayName(ch)
                          }}</v-list-item-title></v-list-item-content
                        >
                      </v-list-item>
                    </template>
                    <span>{{ channelDisplayName(ch) }}</span>
                  </v-tooltip>
                </v-list>
              </template>
              <template v-if="analysisChannels.length > 0">
                <v-divider></v-divider>
                <v-tooltip right color="primary">
                  <template v-slot:activator="{ on }">
                    <div
                      class="section-header"
                      v-on="on"
                      @click="sectionOpen.analyses = !sectionOpen.analyses"
                    >
                      <span>Analyses</span>
                      <v-icon x-small>{{
                        sectionOpen.analyses
                          ? 'mdi-chevron-down'
                          : 'mdi-chevron-right'
                      }}</v-icon>
                    </div>
                  </template>
                  <span>Channels linked to your saved analyses</span>
                </v-tooltip>
                <v-list v-if="sectionOpen.analyses" dense class="py-0">
                  <v-tooltip
                    v-for="ch in analysisChannels"
                    :key="'fs-' + ch.name"
                    right
                    color="primary"
                  >
                    <template v-slot:activator="{ on }">
                      <v-list-item
                        :class="
                          activeChannel === ch.name ? 'primary lighten-5' : ''
                        "
                        v-on="on"
                        @click="selectChannel(ch)"
                      >
                        <v-list-item-content
                          ><v-list-item-title :class="channelTitleClass(ch)">{{
                            channelDisplayName(ch)
                          }}</v-list-item-title></v-list-item-content
                        >
                      </v-list-item>
                    </template>
                    <span>{{ channelDisplayName(ch) }}</span>
                  </v-tooltip>
                </v-list>
              </template>
              <v-divider></v-divider>
              <div
                class="section-header"
                @click="sectionOpen.dms = !sectionOpen.dms"
              >
                <v-tooltip right color="primary">
                  <template v-slot:activator="{ on }">
                    <span style="flex: 1;" v-on="on">Direct Messages</span>
                  </template>
                  <span>Private one-on-one and group conversations</span>
                </v-tooltip>
                <div class="d-flex align-center">
                  <v-tooltip right color="primary">
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        x-small
                        style="margin-right: 2px;"
                        v-on="on"
                        @click.stop="startNewConversation"
                      >
                        <v-icon x-small>mdi-plus</v-icon>
                      </v-btn>
                    </template>
                    <span>Create new group direct message</span>
                  </v-tooltip>
                  <v-icon x-small>{{
                    sectionOpen.dms ? 'mdi-chevron-down' : 'mdi-chevron-right'
                  }}</v-icon>
                </div>
              </div>
              <v-list v-if="sectionOpen.dms" dense class="py-0">
                <v-tooltip
                  v-for="ch in dmChannels"
                  :key="'fs-' + ch.name"
                  right
                  :color="
                    revokedHoverChannel === ch.name ? 'warning' : 'primary'
                  "
                >
                  <template v-slot:activator="{ on }">
                    <v-list-item
                      :class="
                        activeChannel === ch.name ? 'primary lighten-5' : ''
                      "
                      v-on="on"
                      @click="selectChannel(ch)"
                    >
                      <v-list-item-content
                        ><v-list-item-title :class="channelTitleClass(ch)">{{
                          channelDisplayName(ch)
                        }}</v-list-item-title></v-list-item-content
                      >
                      <v-list-item-action
                        v-if="dmHasRevokedMember(ch)"
                        class="my-0 ml-1"
                        style="min-width: 0;"
                        @mouseenter="revokedHoverChannel = ch.name"
                        @mouseleave="revokedHoverChannel = null"
                      >
                        <v-icon x-small color="warning">mdi-account-off</v-icon>
                      </v-list-item-action>
                    </v-list-item>
                  </template>
                  <span v-if="revokedHoverChannel === ch.name"
                    >One or more participants no longer have access to this
                    dataset</span
                  >
                  <span v-else>{{ channelDisplayName(ch) }}</span>
                </v-tooltip>
              </v-list>
            </div>
            <div class="pa-2" style="border-top: 1px solid #e0e0e0;">
              <v-btn x-small text color="primary" @click="openNewChannelDialog">
                <v-icon x-small left>add</v-icon>New Channel
              </v-btn>
            </div>
          </div>

          <!-- Fullscreen message thread / new conversation composer -->
          <div
            style="flex: 1; display: flex; flex-direction: column; overflow: hidden;"
          >
            <!-- Fullscreen new conversation composer -->
            <template v-if="newConversationMode">
              <div
                class="pa-3 d-flex align-center"
                style="border-bottom: 1px solid #e0e0e0; flex-shrink: 0;"
              >
                <span class="subtitle-1 font-weight-bold primary--text"
                  >New Conversation</span
                >
                <v-spacer></v-spacer>
                <v-btn icon small @click="cancelNewConversation">
                  <v-icon small>close</v-icon>
                </v-btn>
              </div>
              <div class="pa-4" style="flex: 1; overflow-y: auto;">
                <div class="body-2 grey--text mb-3">
                  Select one person for a direct message, or multiple people for
                  a group conversation.
                </div>
                <v-autocomplete
                  v-model="newConvSelectedUsers"
                  :items="collectionMembers"
                  :search-input.sync="newConvSearch"
                  item-text="name"
                  item-value="id"
                  label="To:"
                  outlined
                  multiple
                  chips
                  deletable-chips
                  hide-selected
                  hide-details
                  class="mb-4"
                  autofocus
                  @change="newConvSearch = ''"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
                <v-btn
                  color="primary"
                  :disabled="newConvSelectedUsers.length === 0"
                  @click="openNewConversation"
                >
                  Open
                </v-btn>
                <v-btn text class="ml-2" @click="cancelNewConversation"
                  >Cancel</v-btn
                >
              </div>
            </template>

            <!-- Fullscreen normal channel view -->
            <template v-else>
              <div
                class="pa-3 d-flex align-center"
                style="border-bottom: 1px solid #e0e0e0; flex-shrink: 0;"
              >
                <v-menu
                  offset-y
                  :close-on-content-click="false"
                  max-height="300"
                  min-width="240"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      class="subtitle-1 font-weight-bold primary--text channel-header-name"
                      v-bind="attrs"
                      style="cursor: pointer;"
                      v-on="on"
                    >
                      {{ activeChannelHeaderLabel }}
                      <v-icon small color="primary" class="ml-1"
                        >mdi-chevron-down</v-icon
                      >
                    </span>
                  </template>
                  <v-card>
                    <v-card-subtitle
                      class="pb-1 pt-2 subtitle-2 font-weight-bold"
                    >
                      Members ({{ activeChannelMembers.length }})
                    </v-card-subtitle>
                    <v-list dense class="pt-0">
                      <v-list-item
                        v-for="member in activeChannelMembers"
                        :key="member.user_id"
                        class="py-0"
                        style="min-height: 36px;"
                      >
                        <v-list-item-icon class="mr-2 my-1">
                          <v-icon small>mdi-account</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content class="py-1">
                          <v-list-item-title class="body-2">
                            {{ member.name }}
                            <span
                              v-if="
                                currentUser && member.user_id === currentUser.id
                              "
                              class="grey--text"
                              >(you)</span
                            >
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>
                <v-spacer></v-spacer>
                <span
                  v-if="channelEntityTag"
                  class="msg-tag msg-tag--mention channel-entity-tag"
                  :class="
                    channelEntityTag.deleted
                      ? 'msg-tag--deleted'
                      : 'primary--text'
                  "
                  @click="onTagClick(channelEntityTag)"
                  >{{ channelEntityTag.display
                  }}<v-icon
                    x-small
                    :color="channelEntityTag.deleted ? '' : 'primary'"
                    class="msg-tag-icon"
                    >{{ channelEntityTag.icon }}</v-icon
                  ></span
                >
                <v-spacer></v-spacer>
                <v-btn
                  v-if="activeChannelObj && canAddPeople(activeChannelObj)"
                  small
                  text
                  color="primary"
                  class="mr-2"
                  @click="openAddPeople"
                >
                  <v-icon small left>mdi-account-plus</v-icon>Add people
                </v-btn>
                <span v-else class="caption grey--text"
                  >{{ messages.length }} message(s)</span
                >
              </div>
              <div
                ref="messageListFs"
                style="flex: 1; overflow-y: auto; background: #fafafa; padding: 8px 0;"
              >
                <v-container v-if="isLoading" fluid
                  ><v-row justify="center"
                    ><v-progress-circular
                      indeterminate
                      color="primary"
                    ></v-progress-circular></v-row
                ></v-container>
                <div
                  v-else-if="!activeChannel"
                  class="text-center grey--text mt-8"
                >
                  Select a channel to view messages.
                </div>
                <div
                  v-else-if="messages.length === 0"
                  class="text-center grey--text mt-8"
                >
                  No messages yet. Be the first to say something!
                </div>
                <template v-for="(msg, index) in messages">
                  <div
                    v-if="isNewDay(msg, index)"
                    :key="'fs-day-' + msg.id"
                    class="day-banner"
                  >
                    <span class="day-banner-label">{{
                      formatDayLabel(msg.date_generated)
                    }}</span>
                  </div>
                  <div
                    :key="'fs-msg-' + msg.id"
                    :data-message-id="msg.id"
                    class="message-row"
                    :class="{
                      'message-block-start': !isContinuation(msg, index),
                    }"
                  >
                    <div
                      v-if="!isContinuation(msg, index)"
                      class="msg-avatar msg-avatar-lg"
                    >
                      {{ userInitial(msg.user) }}
                    </div>
                    <div class="msg-body">
                      <span
                        v-if="isContinuation(msg, index)"
                        class="msg-continuation-time caption grey--text"
                        >{{ formatTimeShort(msg.date_generated) }}</span
                      >
                      <div
                        v-if="!isContinuation(msg, index)"
                        class="msg-meta d-flex align-center"
                      >
                        <span class="font-weight-bold">{{ msg.user }}</span>
                        <span class="caption grey--text ml-2">{{
                          formatTime(msg.date_generated)
                        }}</span>
                        <span
                          v-if="msg.date_generated !== msg.last_modified"
                          class="caption grey--text ml-1"
                          >(edited)</span
                        >
                        <div class="msg-actions ml-auto">
                          <v-icon
                            v-if="msg.user_id === currentUser.id"
                            small
                            class="mr-1"
                            @click.stop="startEdit(msg)"
                            >edit</v-icon
                          >
                          <v-icon
                            v-if="
                              msg.user_id === currentUser.id ||
                                isCollectionOwner
                            "
                            small
                            @click.stop="deleteMessage(msg)"
                            >delete</v-icon
                          >
                        </div>
                      </div>
                      <div
                        v-if="editingMessageId === msg.id"
                        style="position: relative;"
                      >
                        <div
                          v-if="editMention.active && editMentionItems.length"
                          class="mention-dropdown"
                        >
                          <v-list dense class="py-0">
                            <v-list-item
                              v-for="(item, i) in editMentionItems"
                              :key="item.token"
                              class="px-2"
                              :class="{
                                'mention-item-active':
                                  i === editMentionHighlightIndex,
                              }"
                              @click="selectEditMentionItem(item)"
                              @mouseenter="editMentionHighlightIndex = i"
                            >
                              <v-list-item-icon class="mr-2 my-auto"
                                ><v-icon small>{{
                                  item.icon
                                }}</v-icon></v-list-item-icon
                              >
                              <v-list-item-content>
                                <v-list-item-title class="caption"
                                  >{{ editMention.sigil
                                  }}{{ item.display }}</v-list-item-title
                                >
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </div>
                        <v-textarea
                          ref="editInputFs"
                          v-model="editingContent"
                          dense
                          filled
                          background-color="#f5f5f5"
                          rows="2"
                          :maxlength="maxLength"
                          hide-details
                          @input.native="onEditInput"
                          @keydown.enter.exact.prevent="handleEditEnterKey"
                          @keydown.native="onEditMentionNavKeydown"
                        ></v-textarea>
                        <div class="d-flex mt-1">
                          <v-btn
                            small
                            color="primary"
                            class="mr-1"
                            :disabled="!editingContent.trim()"
                            @click="submitEdit"
                            >Save</v-btn
                          >
                          <v-btn small text @click="cancelEdit">Cancel</v-btn>
                        </div>
                      </div>
                      <!-- no whitespace inside: pre-wrap would render template indent as spaces -->
                      <!-- prettier-ignore -->
                      <div v-else style="white-space: pre-wrap;"><template v-for="(seg, si) in parseContent(msg.content)"><span v-if="seg.type === 'tag'" :key="si" :class="['msg-tag', seg.kind === 'channel' ? 'msg-tag--channel' : 'msg-tag--mention', { 'primary--text': !seg.deleted, 'msg-tag--deleted': seg.deleted }]" @click.stop="onTagClick(seg)">{{ seg.display }}<v-icon x-small :color="seg.deleted ? '' : 'primary'" class="msg-tag-icon">{{ seg.icon }}</v-icon></span><span v-else :key="si">{{ seg.text }}</span></template><span v-if="isContinuation(msg, index) && msg.date_generated !== msg.last_modified" class="caption grey--text ml-1">(edited)</span></div>
                      <div
                        v-if="msg.attachments && msg.attachments.length"
                        class="mt-1"
                      >
                        <img
                          v-for="att in msg.attachments"
                          :key="att.id"
                          :src="att.url"
                          :alt="att.filename"
                          class="msg-attachment-img msg-attachment-img--fs"
                          @click.stop="openImageDialog(att.url)"
                        />
                      </div>
                    </div>
                    <div
                      v-if="isContinuation(msg, index)"
                      class="msg-actions msg-actions-cont"
                    >
                      <v-icon
                        v-if="msg.user_id === currentUser.id"
                        small
                        class="mr-1"
                        @click.stop="startEdit(msg)"
                        >edit</v-icon
                      >
                      <v-icon
                        v-if="
                          msg.user_id === currentUser.id || isCollectionOwner
                        "
                        small
                        @click.stop="deleteMessage(msg)"
                        >delete</v-icon
                      >
                    </div>
                  </div>
                </template>
              </div>
              <div
                class="pa-3"
                style="border-top: 1px solid #e0e0e0; flex-shrink: 0; position: relative;"
                @dragover.prevent
                @drop.prevent="onDrop"
              >
                <!-- Mention / channel autocomplete dropdown -->
                <div
                  v-if="mention.active && mentionItems.length"
                  class="mention-dropdown"
                >
                  <v-list dense class="py-0">
                    <v-list-item
                      v-for="(item, i) in mentionItems"
                      :key="item.token"
                      class="px-2"
                      :class="{
                        'mention-item-active': i === mentionHighlightIndex,
                      }"
                      @click="selectMentionItem(item)"
                      @mouseenter="mentionHighlightIndex = i"
                    >
                      <v-list-item-icon class="mr-2 my-auto"
                        ><v-icon small>{{
                          item.icon
                        }}</v-icon></v-list-item-icon
                      >
                      <v-list-item-content>
                        <v-list-item-title class="caption"
                          >{{ mention.sigil
                          }}{{ item.display }}</v-list-item-title
                        >
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </div>
                <!-- Pending image preview -->
                <div
                  v-if="pendingImagePreview"
                  class="d-flex align-center pa-1 mb-1"
                  style="background: #f5f5f5; border-radius: 4px;"
                >
                  <img
                    :src="pendingImagePreview"
                    style="max-height: 100px; max-width: 160px; border-radius: 4px; object-fit: cover;"
                  />
                  <span
                    class="caption grey--text ml-2"
                    style="flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
                    >{{ pendingImage ? pendingImage.name : '' }}</span
                  >
                  <v-btn icon small @click="clearPendingImage">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
                <v-textarea
                  ref="messageInputFs"
                  v-model="newMessageContent"
                  :placeholder="
                    activeChannel
                      ? `Message ${activeChannelHeaderLabel}`
                      : 'Select a channel first'
                  "
                  :disabled="!activeChannel"
                  dense
                  outlined
                  rows="2"
                  :maxlength="maxLength"
                  hide-details
                  @input.native="onMessageInput"
                  @keydown.enter.exact.prevent="handleEnterKey"
                  @keydown.native="onMentionNavKeydown"
                  @paste.native="onPaste"
                ></v-textarea>
                <div class="d-flex justify-end mt-1">
                  <span class="caption grey--text mr-2 align-self-center"
                    >{{ newMessageContent.length }}/{{ maxLength }} · Enter to
                    send</span
                  >
                  <input
                    ref="imageFileInputFs"
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    style="display: none;"
                    @change="onFileSelected"
                  />
                  <v-btn
                    icon
                    small
                    class="mr-1"
                    title="Attach image"
                    :disabled="!activeChannel"
                    @click="$refs.imageFileInputFs.click()"
                  >
                    <v-icon small>mdi-paperclip</v-icon>
                  </v-btn>
                  <v-btn
                    small
                    color="primary"
                    :disabled="
                      (!newMessageContent.trim() && !pendingImage) ||
                        !activeChannel
                    "
                    @click="sendMessage"
                    >Send</v-btn
                  >
                </div>
              </div>
            </template>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- New Channel dialog -->
    <v-dialog
      v-model="newChannelDialog"
      max-width="440"
      @keydown.esc="newChannelDialog = false"
    >
      <v-card>
        <v-card-title class="subtitle-1 font-weight-bold"
          >New Channel</v-card-title
        >
        <v-card-text class="pb-0">
          <v-text-field
            v-model="newChannelName"
            label="Channel name"
            placeholder="e.g. project-notes"
            dense
            outlined
            hide-details
            class="mb-3"
            autofocus
            @keyup.enter="createChannel"
          ></v-text-field>
          <v-autocomplete
            v-model="newChannelMembers"
            :items="collectionMembers"
            :search-input.sync="newChannelSearch"
            item-text="name"
            item-value="id"
            label="Add members (optional)"
            dense
            outlined
            multiple
            chips
            small-chips
            deletable-chips
            hide-selected
            hide-details
            class="mb-1"
            @change="newChannelSearch = ''"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <div class="d-flex justify-end mt-1">
            <v-btn x-small text color="primary" @click="addAllMembers"
              >Add all</v-btn
            >
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small @click="newChannelDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            small
            :disabled="!newChannelName.trim()"
            @click="createChannel"
            >Create</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add people dialog -->
    <v-dialog
      v-model="addPeopleDialog"
      max-width="400"
      @keydown.esc="addPeopleDialog = false"
    >
      <v-card>
        <v-card-title class="subtitle-1 font-weight-bold"
          >Add people</v-card-title
        >
        <v-card-text>
          <v-autocomplete
            v-model="addPeopleSelected"
            :items="addPeopleOptions"
            :search-input.sync="addPeopleSearch"
            item-text="name"
            item-value="id"
            label="Select people to add"
            dense
            outlined
            multiple
            chips
            small-chips
            deletable-chips
            hide-selected
            hide-details
            autofocus
            @change="addPeopleSearch = ''"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small @click="addPeopleDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            small
            :disabled="addPeopleSelected.length === 0"
            @click="submitAddPeople"
            >Add</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Leave channel confirmation dialog -->
    <v-dialog v-model="leaveDialog" max-width="360">
      <v-card>
        <v-card-title class="subtitle-1 font-weight-bold"
          >Leave channel?</v-card-title
        >
        <v-card-text>
          You will no longer see messages in
          <strong>{{
            leaveTargetChannel ? '#' + leaveTargetChannel.name : ''
          }}</strong
          >.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small @click="leaveDialog = false">Cancel</v-btn>
          <v-btn color="error" small @click="executeLeave">Leave</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Image lightbox dialog -->
    <v-dialog
      v-model="imageDialogOpen"
      content-class="image-lightbox-dialog"
      @click:outside="imageDialogOpen = false"
    >
      <img
        v-if="imageDialogSrc"
        :src="imageDialogSrc"
        style="max-width: 90vw; max-height: 90vh; display: block; cursor: pointer;"
        @click="imageDialogOpen = false"
      />
    </v-dialog>

    <!-- Guided tour overlay (rendered fullscreen at the app root so it
         escapes the navigation drawer's transform and covers the whole
         viewport, blocking interaction with the rest of the app) -->
    <v-dialog
      v-model="tourActive"
      fullscreen
      hide-overlay
      persistent
      no-click-animation
      transition="fade-transition"
    >
      <chat-tour v-if="tourActive" @close="tourActive = false"></chat-tour>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import { state as authState } from '@/store/modules/auth/types';
import {
  actions,
  getters,
  mutations,
} from '@/store/modules/collaboration/types';
import { format } from 'date-fns';
import { resolveTagsForEdit } from '@/utils/resolveTags';
import CollectionSwitcherMenu from './CollectionSwitcherMenu.vue';
import ChatEmptyState from './ChatEmptyState.vue';
import ChatTour from './ChatTour.vue';

const TAG_ICONS = {
  user: 'mdi-account',
  cohort: 'mdi-account-group',
  analysis: 'mdi-chart-bar',
  channel: 'mdi-pound',
};

export default {
  name: 'ChatPanel',
  components: { CollectionSwitcherMenu, ChatEmptyState, ChatTour },
  data() {
    return {
      isLoading: false,
      fullscreen: false,
      tourActive: false,
      newMessageContent: '',
      editingMessageId: null,
      editingContent: '',
      maxLength: 1900,
      availableCollections: [],
      revokedHoverChannel: null,
      sectionOpen: {
        channels: true,
        cohorts: false,
        analyses: false,
        dms: true,
      },
      // New channel dialog
      newChannelDialog: false,
      newChannelName: '',
      newChannelMembers: [],
      // New conversation (DM/group DM)
      newConvSelectedUsers: [],
      newConvSearch: '',
      // Add people dialog
      addPeopleDialog: false,
      addPeopleSelected: [],
      addPeopleSearch: '',
      newChannelSearch: '',
      // Leave channel dialog
      leaveDialog: false,
      leaveTargetChannel: null,
      // Mention / channel autocomplete (new message input)
      mention: { active: false, sigil: '', query: '', start: -1 },
      mentionHighlightIndex: 0,
      // Mention / channel autocomplete (edit textarea)
      editMention: { active: false, sigil: '', query: '', start: -1 },
      editMentionHighlightIndex: 0,
      // Image attachment
      pendingImage: null,
      pendingImagePreview: null,
      imageDialogSrc: null,
      imageDialogOpen: false,
    };
  },
  computed: {
    ...mapState('auth', {
      currentUser: authState.USER,
    }),
    activeCollection() {
      return this.$store.getters['collaboration/' + getters.ACTIVE_COLLECTION];
    },
    channels() {
      return this.$store.getters['collaboration/' + getters.CHANNELS];
    },
    collectionMembers() {
      return this.$store.getters['collaboration/' + getters.COLLECTION_MEMBERS];
    },
    usersById() {
      return this.$store.getters['collaboration/' + getters.USERS_BY_ID];
    },
    newConversationMode() {
      return this.$store.getters[
        'collaboration/' + getters.NEW_CONVERSATION_MODE
      ];
    },
    generalAndOtherChannels() {
      return this.channels.filter(ch => ['general', 'other'].includes(ch.type));
    },
    cohortChannels() {
      return this.channels.filter(ch => ch.type === 'cohort');
    },
    analysisChannels() {
      return this.channels.filter(ch => ch.type === 'analysis');
    },
    dmChannels() {
      return this.channels.filter(ch =>
        ['direct', 'group_dm'].includes(ch.type)
      );
    },
    unreadChannelIds() {
      const userId = this.currentUser ? this.currentUser.id : null;
      const unread =
        this.$store.getters['collaboration/' + getters.UNREAD_FEED] || [];
      return new Set(
        unread.filter(m => m.user_id !== userId).map(m => m.channel_id)
      );
    },
    messages() {
      return this.$store.getters['collaboration/' + getters.MESSAGES];
    },
    activeChannel() {
      return this.$store.getters['collaboration/' + getters.ACTIVE_CHANNEL];
    },
    activeChannelObj() {
      return this.channels.find(ch => ch.name === this.activeChannel) || null;
    },
    activeChannelHeaderLabel() {
      if (!this.activeChannelObj) return 'Select a channel';
      return this.channelDisplayName(this.activeChannelObj);
    },
    // Entity link shown in the header of cohort/analysis channels, pointing
    // to the underlying study group or analysis. Rendered like a message
    // mention tag and reuses onTagClick for navigation.
    channelEntityTag() {
      const ch = this.activeChannelObj;
      if (!ch) return null;
      if (ch.type === 'cohort' && ch.cohort_id != null) {
        return this.resolveTag(`@cohort:${ch.cohort_id}`);
      }
      if (ch.type === 'analysis' && ch.analysis_id != null) {
        return this.resolveTag(`@analysis:${ch.analysis_id}`);
      }
      return null;
    },
    isCollectionOwner() {
      return (
        this.currentUser &&
        this.activeCollection &&
        this.currentUser.id === this.activeCollection.user_id
      );
    },
    // Members available to add via "Add people" — excludes users already in the channel
    addPeopleOptions() {
      if (!this.activeChannelObj) return this.collectionMembers;
      const existingIds = new Set(
        (this.activeChannelObj.members || []).map(m => m.user_id)
      );
      existingIds.add(this.currentUser.id);
      return this.collectionMembers.filter(m => !existingIds.has(m.id));
    },
    activeChannelMembers() {
      if (!this.activeChannelObj) return [];
      const members = (this.activeChannelObj.members || []).map(m => ({
        user_id: m.user_id,
        name: m.user,
      }));
      // Sort: current user first, then alphabetical
      members.sort((a, b) => {
        if (this.currentUser && a.user_id === this.currentUser.id) return -1;
        if (this.currentUser && b.user_id === this.currentUser.id) return 1;
        return a.name.localeCompare(b.name);
      });
      return members;
    },
    mentionItems() {
      return this._mentionItemsFor(this.mention);
    },
    editMentionItems() {
      return this._mentionItemsFor(this.editMention);
    },
    scrollTarget() {
      return this.$store.getters[
        'collaboration/' + getters.SCROLL_TARGET_MESSAGE_ID
      ];
    },
  },
  watch: {
    'activeCollection.id': {
      handler(id, oldId) {
        // Watch the ID (primitive) so same-collection object replacements don't trigger a reload.
        if (id && id !== oldId) {
          this.loadChannels(id);
          this.$store.dispatch('collaboration/' + actions.FETCH_MEMBERS, id);
        }
      },
    },
    activeChannelObj(ch) {
      if (!ch) return;
      if (ch.type === 'cohort') this.sectionOpen.cohorts = true;
      else if (ch.type === 'analysis') this.sectionOpen.analyses = true;
      else if (['direct', 'group_dm'].includes(ch.type))
        this.sectionOpen.dms = true;
      else this.sectionOpen.channels = true;
    },
    fullscreen(open) {
      if (open) {
        this.$nextTick(() => this.scrollToBottom(true));
      }
    },
  },
  created() {
    this.fetchAvailableCollections();
    if (this.activeCollection) {
      this.loadChannels(this.activeCollection.id);
      this.$store.dispatch(
        'collaboration/' + actions.FETCH_MEMBERS,
        this.activeCollection.id
      );
    }
  },
  beforeDestroy() {
    this.$store.dispatch('collaboration/' + actions.STOP_POLLING);
  },
  methods: {
    channelTitleClass(ch) {
      if (this.activeChannel === ch.name)
        return 'primary--text font-weight-bold';
      if (this.unreadChannelIds.has(ch.id)) return 'font-weight-bold';
      return '';
    },
    async fetchAvailableCollections() {
      try {
        const { data } = await axios.get('/api/collections');
        if (data && data.collections) {
          // Only show shared collections (group-based or public) — chat is only
          // meaningful when more than one user has access to the dataset.
          this.availableCollections = data.collections.filter(
            c => c.group_id != null || c.is_public
          );
          // If the active collection is no longer accessible (e.g. access was
          // revoked, or a different user logged in without a full page refresh),
          // clear it so the empty-state prompt is shown.
          if (
            this.activeCollection &&
            !this.availableCollections.some(
              c => c.id === this.activeCollection.id
            )
          ) {
            this.$store.commit(
              'collaboration/' + mutations.SET_ACTIVE_COLLECTION,
              null
            );
          }
        }
      } catch (_e) {
        // silently ignore
      }
    },
    async onPickCollection(col) {
      if (!col) return;
      await this.$store.dispatch('collaboration/' + actions.OPEN_CHAT, col);
    },
    // Deselect the current dataset, returning the panel to its welcome state.
    onClearCollection() {
      this.$store.dispatch('collaboration/' + actions.STOP_POLLING);
      this.$store.dispatch(
        'collaboration/' + actions.SET_ACTIVE_COLLECTION,
        null
      );
    },
    async loadChannels(collectionId) {
      this.isLoading = true;
      await this.$store.dispatch(
        'collaboration/' + actions.FETCH_CHANNELS,
        collectionId
      );
      const channels = this.$store.getters['collaboration/' + getters.CHANNELS];
      // If there's a pending scroll target, navigate to that channel instead
      const target = this.scrollTarget;
      let targetCh = null;
      if (target) {
        targetCh = channels.find(c => c.name === target.channelName);
      }
      const selectedCh =
        targetCh ||
        channels.find(c => c.type === 'general') ||
        (channels.length > 0 ? channels[0] : null);
      if (selectedCh) {
        await this.$store.dispatch(
          'collaboration/' + actions.SET_ACTIVE_CHANNEL,
          {
            collectionId,
            channelName: selectedCh.name,
          }
        );
        this.$store.dispatch('collaboration/' + actions.MARK_CHANNEL_READ, {
          channelId: selectedCh.id,
        });
      }
      this.isLoading = false;
      this.$store.dispatch('collaboration/' + actions.START_POLLING);
      if (target && targetCh) {
        this.$nextTick(() => this.scrollToMessage(target.messageId));
      } else {
        this.$nextTick(() => this.scrollToBottom());
      }
    },
    async selectChannel(ch) {
      this.$store.dispatch('collaboration/' + actions.EXIT_NEW_CONVERSATION);
      this.isLoading = true;
      await this.$store.dispatch(
        'collaboration/' + actions.SET_ACTIVE_CHANNEL,
        {
          collectionId: this.activeCollection.id,
          channelName: ch.name,
        }
      );
      this.$store.dispatch('collaboration/' + actions.MARK_CHANNEL_READ, {
        channelId: ch.id,
      });
      this.isLoading = false;
      this.$nextTick(() => this.scrollToBottom());
    },

    // --- New channel ---
    openNewChannelDialog() {
      this.newChannelName = '';
      this.newChannelMembers = [];
      this.newChannelDialog = true;
    },
    addAllMembers() {
      this.newChannelMembers = this.collectionMembers.map(m => m.id);
    },
    async createChannel() {
      const name = this.newChannelName
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-');
      if (!name) return;
      await this.$store.dispatch('collaboration/' + actions.CREATE_CHANNEL, {
        collectionId: this.activeCollection.id,
        name,
        userIds: this.newChannelMembers,
      });
      this.newChannelDialog = false;
      this.newChannelName = '';
      this.newChannelMembers = [];
      const ch = this.$store.getters['collaboration/' + getters.CHANNELS].find(
        c => c.name === name
      );
      if (ch) await this.selectChannel(ch);
    },

    // --- New conversation (DM / group DM) ---
    startNewConversation() {
      this.newConvSelectedUsers = [];
      this.sectionOpen.dms = true;
      this.$store.dispatch('collaboration/' + actions.ENTER_NEW_CONVERSATION);
    },
    cancelNewConversation() {
      this.$store.dispatch('collaboration/' + actions.EXIT_NEW_CONVERSATION);
      // Re-select general channel so the view isn't blank
      const general = this.channels.find(c => c.type === 'general');
      if (general) this.selectChannel(general);
    },
    async openNewConversation() {
      if (!this.newConvSelectedUsers.length) return;
      const channel = await this.$store.dispatch(
        'collaboration/' + actions.FIND_OR_CREATE_DM,
        {
          collectionId: this.activeCollection.id,
          userIds: this.newConvSelectedUsers,
        }
      );
      if (channel) {
        await this.selectChannel(channel);
      }
      this.newConvSelectedUsers = [];
    },

    // --- Add people ---
    canAddPeople(ch) {
      return ['other', 'direct', 'group_dm'].includes(ch.type);
    },
    openAddPeople() {
      this.addPeopleSelected = [];
      this.addPeopleDialog = true;
    },
    async submitAddPeople() {
      if (!this.addPeopleSelected.length || !this.activeChannelObj) return;
      const ch = this.activeChannelObj;

      if (ch.type === 'other') {
        await this.$store.dispatch(
          'collaboration/' + actions.ADD_CHANNEL_MEMBERS,
          {
            collectionId: this.activeCollection.id,
            channelName: ch.name,
            userIds: this.addPeopleSelected,
          }
        );
        this.addPeopleDialog = false;
      } else {
        // For direct / group_dm: create/navigate to expanded group DM
        this.addPeopleDialog = false;
        // Collect all current participant IDs except self, plus new selections
        const currentOtherIds = (ch.members || [])
          .map(m => m.user_id)
          .filter(id => id !== this.currentUser.id);
        const allOtherIds = [
          ...new Set([...currentOtherIds, ...this.addPeopleSelected]),
        ];
        const channel = await this.$store.dispatch(
          'collaboration/' + actions.FIND_OR_CREATE_DM,
          {
            collectionId: this.activeCollection.id,
            userIds: allOtherIds,
          }
        );
        if (channel) await this.selectChannel(channel);
      }
    },

    // --- Leave channel ---
    confirmLeave(ch) {
      this.leaveTargetChannel = ch;
      this.leaveDialog = true;
    },
    async executeLeave() {
      const ch = this.leaveTargetChannel;
      this.leaveDialog = false;
      this.leaveTargetChannel = null;
      await this.$store.dispatch('collaboration/' + actions.LEAVE_CHANNEL, {
        collectionId: this.activeCollection.id,
        channelName: ch.name,
      });
      // If we left the active channel, navigate to general
      if (this.activeChannel === ch.name) {
        const general = this.channels.find(c => c.type === 'general');
        if (general) await this.selectChannel(general);
      }
    },

    // --- Messages ---
    async sendMessage() {
      if (!this.newMessageContent.trim() && !this.pendingImage) return;
      if (!this.activeChannel) return;
      const content = this.preprocessContent(this.newMessageContent.trim());
      const imageFile = this.pendingImage;
      this.newMessageContent = '';
      this.clearPendingImage();
      await this.$store.dispatch('collaboration/' + actions.POST_MESSAGE, {
        collectionId: this.activeCollection.id,
        channelName: this.activeChannel,
        content,
        imageFile,
      });
      this.$nextTick(() => this.scrollToBottom());
    },

    // --- Image attachment ---
    onFileSelected(event) {
      const file = event.target.files[0];
      if (file) this.setPendingImage(file);
      event.target.value = '';
    },
    onPaste(event) {
      const items = event.clipboardData && event.clipboardData.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          event.preventDefault();
          this.setPendingImage(item.getAsFile());
          return;
        }
      }
    },
    onDrop(event) {
      const file =
        event.dataTransfer && event.dataTransfer.files
          ? event.dataTransfer.files[0]
          : null;
      if (file && file.type.startsWith('image/')) {
        this.setPendingImage(file);
      }
    },
    setPendingImage(file) {
      const maxSize = 10 * 1024 * 1024;
      const maxDim = 2048;
      const downscaleThreshold = 5 * 1024 * 1024;

      // Load image to check dimensions and optionally downscale
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const needsResize =
          file.type !== 'image/gif' &&
          (file.size > downscaleThreshold ||
            img.width > maxDim ||
            img.height > maxDim);

        if (needsResize) {
          // Downscale using canvas
          let w = img.width;
          let h = img.height;
          if (w > maxDim || h > maxDim) {
            const scale = maxDim / Math.max(w, h);
            w = Math.round(w * scale);
            h = Math.round(h * scale);
          }
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          // Use PNG for images that might have transparency, JPEG otherwise
          const outputType =
            file.type === 'image/png' || file.type === 'image/webp'
              ? 'image/png'
              : 'image/jpeg';
          canvas.toBlob(
            blob => {
              if (blob && blob.size <= maxSize) {
                blob.name = file.name;
                this.pendingImage = blob;
                this.pendingImagePreview = URL.createObjectURL(blob);
              }
              URL.revokeObjectURL(url);
            },
            outputType,
            0.85
          );
        } else if (file.size <= maxSize) {
          this.pendingImage = file;
          this.pendingImagePreview = url;
        } else {
          URL.revokeObjectURL(url);
        }
      };
      img.onerror = () => URL.revokeObjectURL(url);
      img.src = url;
    },
    clearPendingImage() {
      if (this.pendingImagePreview) {
        URL.revokeObjectURL(this.pendingImagePreview);
      }
      this.pendingImage = null;
      this.pendingImagePreview = null;
    },
    openImageDialog(url) {
      this.imageDialogSrc = url;
      this.imageDialogOpen = true;
    },

    // --- Mention / channel autocomplete ---
    _mentionItemsFor(mention) {
      if (!mention.active) return [];
      const q = mention.query.toLowerCase();
      if (mention.sigil === '@') {
        const self = this.currentUser
          ? [{ id: this.currentUser.id, name: this.currentUser.name }]
          : [];
        const users = [...self, ...this.collectionMembers]
          .filter(m => m.name.toLowerCase().includes(q))
          .map(m => ({
            type: 'user',
            token: `@user:${m.id}`,
            display: m.name,
            icon: TAG_ICONS.user,
          }));
        const cohorts = this.cohortChannels
          .filter(
            ch => ch.cohort_label && ch.cohort_label.toLowerCase().includes(q)
          )
          .map(ch => ({
            type: 'cohort',
            token: `@cohort:${ch.cohort_id}`,
            display: this.channelDisplayName(ch),
            icon: TAG_ICONS.cohort,
          }));
        const analyses = this.analysisChannels
          .filter(ch => `analysis ${ch.analysis_num}`.toLowerCase().includes(q))
          .map(ch => ({
            type: 'analysis',
            token: `@analysis:${ch.analysis_id}`,
            display: `Analysis ${ch.analysis_num}`,
            icon: TAG_ICONS.analysis,
          }));
        return [...users, ...cohorts, ...analyses].slice(0, 8);
      }
      return this.channels
        .filter(ch => !['direct', 'group_dm'].includes(ch.type))
        .filter(ch =>
          this.channelDisplayName(ch)
            .toLowerCase()
            .includes(q)
        )
        .map(ch => ({
          type: 'channel',
          token: `#${ch.name}`,
          display: this.channelDisplayName(ch),
          icon:
            ch.type === 'cohort'
              ? TAG_ICONS.cohort
              : ch.type === 'analysis'
              ? TAG_ICONS.analysis
              : TAG_ICONS.channel,
        }))
        .slice(0, 8);
    },
    onMessageInput(event) {
      const textarea = event.target;
      const text = textarea.value;
      const cursorPos = textarea.selectionStart;
      let i = cursorPos - 1;
      while (i >= 0) {
        const ch = text[i];
        if (ch === ' ' || ch === '\n') break;
        if (ch === '@' || ch === '#') {
          this.mention = {
            active: true,
            sigil: ch,
            query: text.slice(i + 1, cursorPos),
            start: i,
          };
          this.mentionHighlightIndex = 0;
          return;
        }
        i--;
      }
      this.mention = { active: false, sigil: '', query: '', start: -1 };
    },
    handleEnterKey() {
      if (this.mention.active && this.mentionItems.length) {
        this.selectMentionItem(
          this.mentionItems[this.mentionHighlightIndex] || this.mentionItems[0]
        );
      } else {
        this.sendMessage();
      }
    },
    onMentionNavKeydown(event) {
      if (!this.mention.active || !this.mentionItems.length) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.mentionHighlightIndex = Math.min(
          this.mentionHighlightIndex + 1,
          this.mentionItems.length - 1
        );
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.mentionHighlightIndex = Math.max(
          this.mentionHighlightIndex - 1,
          0
        );
      } else if (event.key === 'Escape') {
        event.stopPropagation();
        this.mention = { active: false, sigil: '', query: '', start: -1 };
      }
    },
    selectMentionItem(item) {
      if (!item) return;
      const text = this.newMessageContent;
      const before = text.slice(0, this.mention.start);
      const after = text.slice(
        this.mention.start + 1 + this.mention.query.length
      );
      const inserted =
        item.type === 'channel'
          ? `#${item.display}`
          : `${this.mention.sigil}${item.display.replace(/[^a-zA-Z0-9]/g, '')}`;
      this.newMessageContent = `${before}${inserted} ${after}`;
      this.mention = { active: false, sigil: '', query: '', start: -1 };
      this.$nextTick(() => {
        const ref = this.fullscreen
          ? this.$refs.messageInputFs
          : this.$refs.messageInput;
        if (ref) {
          const ta = ref.$el.querySelector('textarea');
          const pos = before.length + inserted.length + 1;
          ta.focus();
          ta.setSelectionRange(pos, pos);
        }
      });
    },
    // Convert display-form mentions/channels to stored tokens
    preprocessContent(text) {
      const normalize = s => s.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      const allUsers = [
        ...(this.currentUser ? [this.currentUser] : []),
        ...this.collectionMembers,
      ];
      // Convert @ display names to tokens
      let result = text.replace(/@(\w+)/g, (match, slug) => {
        const s = slug.toLowerCase();
        const user = allUsers.find(m => normalize(m.name) === s);
        if (user) return `@user:${user.id}`;
        const cohort = this.cohortChannels.find(
          ch =>
            ch.cohort_label &&
            (normalize(ch.cohort_label) === s ||
              normalize(ch.cohort_label) === 'o' + s)
        );
        if (cohort) return `@cohort:${cohort.cohort_id}`;
        const analysis = this.analysisChannels.find(
          ch => `analysis${ch.analysis_num}` === s
        );
        if (analysis) return `@analysis:${analysis.analysis_id}`;
        return match;
      });
      // Convert # channel display names back to stable tokens.
      // Build longest-first list so e.g. "#Analysis 10" matches before "#Analysis 1".
      const channelMappings = [];
      this.channels.forEach(ch => {
        let display = null;
        if (ch.type === 'cohort' && ch.cohort_label)
          display = `#${this.channelDisplayName(ch)}`;
        else if (ch.type === 'analysis' && ch.analysis_num != null)
          display = `#Analysis ${ch.analysis_num}`;
        if (display) channelMappings.push({ display, token: `#${ch.name}` });
      });
      channelMappings.sort((a, b) => b.display.length - a.display.length);
      channelMappings.forEach(({ display, token }) => {
        result = result.split(display).join(token);
      });
      return result;
    },

    // --- Edit-mode autocomplete ---
    onEditInput(event) {
      const textarea = event.target;
      const text = textarea.value;
      const cursorPos = textarea.selectionStart;
      let i = cursorPos - 1;
      while (i >= 0) {
        const ch = text[i];
        if (ch === ' ' || ch === '\n') break;
        if (ch === '@' || ch === '#') {
          this.editMention = {
            active: true,
            sigil: ch,
            query: text.slice(i + 1, cursorPos),
            start: i,
          };
          this.editMentionHighlightIndex = 0;
          return;
        }
        i--;
      }
      this.editMention = { active: false, sigil: '', query: '', start: -1 };
    },
    handleEditEnterKey() {
      if (this.editMention.active && this.editMentionItems.length) {
        this.selectEditMentionItem(
          this.editMentionItems[this.editMentionHighlightIndex] ||
            this.editMentionItems[0]
        );
      } else {
        this.submitEdit();
      }
    },
    onEditMentionNavKeydown(event) {
      if (!this.editMention.active || !this.editMentionItems.length) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.editMentionHighlightIndex = Math.min(
          this.editMentionHighlightIndex + 1,
          this.editMentionItems.length - 1
        );
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.editMentionHighlightIndex = Math.max(
          this.editMentionHighlightIndex - 1,
          0
        );
      } else if (event.key === 'Escape') {
        event.stopPropagation();
        this.editMention = { active: false, sigil: '', query: '', start: -1 };
      }
    },
    selectEditMentionItem(item) {
      if (!item) return;
      const text = this.editingContent;
      const before = text.slice(0, this.editMention.start);
      const after = text.slice(
        this.editMention.start + 1 + this.editMention.query.length
      );
      const inserted =
        item.type === 'channel'
          ? `#${item.display}`
          : `${this.editMention.sigil}${item.display.replace(
              /[^a-zA-Z0-9]/g,
              ''
            )}`;
      this.editingContent = `${before}${inserted} ${after}`;
      this.editMention = { active: false, sigil: '', query: '', start: -1 };
      this.$nextTick(() => {
        var ref = this.fullscreen
          ? this.$refs.editInputFs
          : this.$refs.editInputDocked;

        if (ref) {
          if (Array.isArray(ref) && ref.length > 0) {
            ref = ref[0];
            const ta = ref.$el.querySelector('textarea');
            const pos = before.length + inserted.length + 1;
            ta.focus();
            ta.setSelectionRange(pos, pos);
          }
        }
      });
    },

    // --- Tag rendering in messages ---
    resolveTag(raw) {
      if (raw.startsWith('@user:')) {
        const id = parseInt(raw.slice(6));
        const member =
          this.collectionMembers.find(m => m.id === id) ||
          (this.currentUser && this.currentUser.id === id
            ? this.currentUser
            : null);
        return {
          display: member ? `@${member.name}` : raw,
          deleted: !member,
          kind: 'mention',
          icon: TAG_ICONS.user,
          clickTarget: { type: 'user', id },
        };
      }
      if (raw.startsWith('@cohort:')) {
        const id = parseInt(raw.slice(8));
        const ch = this.cohortChannels.find(c => c.cohort_id === id);
        return {
          display: ch ? `@${this.channelDisplayName(ch)}` : raw,
          deleted: !ch || !!ch.is_archived,
          kind: 'mention',
          icon: TAG_ICONS.cohort,
          clickTarget: { type: 'cohort', id },
        };
      }
      if (raw.startsWith('@analysis:')) {
        const id = parseInt(raw.slice(10));
        const ch = this.analysisChannels.find(c => c.analysis_id === id);
        return {
          display: ch ? `@Analysis ${ch.analysis_num}` : raw,
          deleted: !ch || !!ch.is_archived,
          kind: 'mention',
          icon: TAG_ICONS.analysis,
          clickTarget: {
            type: 'analysis',
            id,
            num: ch ? ch.analysis_num : null,
          },
        };
      }
      if (raw.startsWith('#')) {
        const name = raw.slice(1);
        const ch = this.channels.find(c => c.name === name);
        const iconType =
          ch && ch.type === 'cohort'
            ? 'cohort'
            : ch && ch.type === 'analysis'
            ? 'analysis'
            : 'channel';
        return {
          display: ch ? `#${this.channelDisplayName(ch)}` : raw,
          deleted: !ch || !!ch.is_archived,
          kind: 'channel',
          icon: TAG_ICONS[iconType],
          clickTarget: { type: 'channel', name },
        };
      }
      return {
        display: raw,
        deleted: false,
        kind: 'mention',
        icon: TAG_ICONS.user,
        clickTarget: null,
      };
    },
    parseContent(content) {
      const parts = content.split(
        /(@user:\d+|@cohort:\d+|@analysis:\d+|#[\w-]+)/g
      );
      return parts.map((part, i) => {
        if (i % 2 === 0) return { type: 'text', text: part };
        return { type: 'tag', ...this.resolveTag(part) };
      });
    },
    onTagClick(seg) {
      if (seg.deleted || !seg.clickTarget) return;
      const { type, id, name, num } = seg.clickTarget;
      if (type === 'user') {
        this.$store
          .dispatch('collaboration/' + actions.FIND_OR_CREATE_DM, {
            collectionId: this.activeCollection.id,
            userIds: [id],
          })
          .then(channel => {
            if (channel) {
              this.$store.dispatch(
                'collaboration/' + actions.SET_ACTIVE_CHANNEL,
                {
                  collectionId: this.activeCollection.id,
                  channelName: channel.name,
                }
              );
            }
          });
      } else if (type === 'channel') {
        this.$store.dispatch('collaboration/' + actions.SET_ACTIVE_CHANNEL, {
          collectionId: this.activeCollection.id,
          channelName: name,
        });
      } else if (type === 'analysis') {
        const collectionId = this.activeCollection && this.activeCollection.id;
        if (!collectionId || !num) return;
        this.$router
          .push({
            name: 'dataExplorer',
            query: { collection: collectionId, scrollTo: num },
          })
          .catch(() => {});
      } else if (type === 'cohort') {
        const collectionId = this.activeCollection && this.activeCollection.id;
        if (!collectionId || !id) return;
        this.$router
          .push({
            name: 'cohortManager',
            query: { collection: collectionId, cohortId: id },
          })
          .catch(() => {});
      }
    },
    startEdit(msg) {
      this.editingMessageId = msg.id;
      this.editingContent = resolveTagsForEdit(
        msg.content,
        this.channels,
        this.usersById
      );
      this.editMention = { active: false, sigil: '', query: '', start: -1 };
    },
    cancelEdit() {
      this.editingMessageId = null;
      this.editingContent = '';
      this.editMention = { active: false, sigil: '', query: '', start: -1 };
    },
    async submitEdit() {
      if (!this.editingContent.trim()) return;
      await this.$store.dispatch('collaboration/' + actions.EDIT_MESSAGE, {
        collectionId: this.activeCollection.id,
        channelName: this.activeChannel,
        messageId: this.editingMessageId,
        content: this.preprocessContent(this.editingContent.trim()),
      });
      this.cancelEdit();
    },
    async deleteMessage(msg) {
      await this.$store.dispatch('collaboration/' + actions.DELETE_MESSAGE, {
        collectionId: this.activeCollection.id,
        channelName: this.activeChannel,
        messageId: msg.id,
      });
    },
    closePanel() {
      this.$store.dispatch('collaboration/' + actions.CLOSE_CHAT);
    },
    openFullscreen() {
      this.fullscreen = true;
    },
    startTour() {
      this.tourActive = true;
    },
    scrollToBottom(fullscreenMode = false) {
      const ref = fullscreenMode
        ? this.$refs.messageListFs
        : this.$refs.messageList;
      if (ref) ref.scrollTop = ref.scrollHeight;
    },
    scrollToMessage(messageId) {
      this.$nextTick(() => {
        const el = document.querySelector(`[data-message-id="${messageId}"]`);
        if (el) {
          el.scrollIntoView({ block: 'center' });
          el.classList.add('highlight-message');
          setTimeout(() => el.classList.remove('highlight-message'), 2000);
        }
        // Clear the scroll target
        this.$store.commit(
          'collaboration/' + mutations.SET_SCROLL_TARGET_MESSAGE_ID,
          null
        );
      });
    },
    userInitial(name) {
      return name ? name.charAt(0).toUpperCase() : '?';
    },
    dmHasRevokedMember(ch) {
      return (ch.members || []).some(
        m => m.user_id !== this.currentUser.id && m.revoked
      );
    },
    channelDisplayName(ch) {
      if (ch.type === 'direct') {
        const other = (ch.members || []).find(
          m => m.user_id !== this.currentUser.id
        );
        return other ? other.user : ch.name;
      }
      if (ch.type === 'group_dm') {
        const others = (ch.members || []).filter(
          m => m.user_id !== this.currentUser.id
        );
        return others.length > 0
          ? others.map(m => m.user).join(', ')
          : this.currentUser.name + ' (you)';
      }
      if (ch.type === 'cohort') {
        const label = ch.cohort_label || ch.name;
        return label.replace(/^\[O\]_/, '');
      }
      if (ch.type === 'analysis')
        return ch.analysis_num != null
          ? `Analysis ${ch.analysis_num}`
          : ch.name;
      return ch.name;
    },
    isContinuation(msg, index) {
      if (index === 0) return false;
      const prev = this.messages[index - 1];
      if (prev.user_id !== msg.user_id) return false;
      const diff = new Date(msg.date_generated) - new Date(prev.date_generated);
      return diff < 5 * 60 * 1000;
    },
    isNewDay(msg, index) {
      if (index === 0) return true;
      const prev = this.messages[index - 1];
      const p = new Date(prev.date_generated);
      const c = new Date(msg.date_generated);
      return (
        p.getFullYear() !== c.getFullYear() ||
        p.getMonth() !== c.getMonth() ||
        p.getDate() !== c.getDate()
      );
    },
    formatDayLabel(d) {
      const date = new Date(d);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const msgDay = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      );
      if (msgDay.getTime() === today.getTime()) return 'Today';
      if (msgDay.getTime() === yesterday.getTime()) return 'Yesterday';
      return format(date, 'MMMM d, yyyy');
    },
    formatTime(d) {
      if (!d) return '';
      return format(new Date(d), 'h:mm a');
    },
    formatTimeShort(d) {
      if (!d) return '';
      return format(new Date(d), 'h:mm');
    },
  },
};
</script>

<style scoped>
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  user-select: none;
  color: var(--v-primary-base);
}
.section-header:hover {
  background: rgba(0, 0, 0, 0.04);
}
.v-list-item {
  cursor: pointer;
  min-height: 32px !important;
}
.leave-btn {
  opacity: 0;
  transition: opacity 0.1s;
}
.v-list-item:hover .leave-btn {
  opacity: 1;
}

/* Day separator banner */
.day-banner {
  display: flex;
  align-items: center;
  margin: 8px 12px 4px;
}
.day-banner::before,
.day-banner::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e0e0e0;
}
.day-banner-label {
  font-size: 0.65rem;
  color: #757575;
  padding: 2px 8px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  margin: 0 8px;
  white-space: nowrap;
}

/* Slack-style message rows */
.message-row {
  position: relative;
  padding: 2px 12px 2px 40px;
  border-radius: 4px;
}
.message-block-start {
  margin-top: 5px;
}
.message-row:hover {
  background: rgba(0, 0, 0, 0.08);
}
.message-row:hover .msg-actions {
  opacity: 1;
}
.message-row:hover .msg-continuation-time {
  opacity: 1;
}
.msg-avatar {
  position: absolute;
  left: 12px;
  top: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--v-primary-base);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5rem;
  font-weight: 600;
}
.msg-avatar-lg {
  width: 20px;
  height: 20px;
  font-size: 0.6rem;
}
.msg-body {
  min-width: 0;
  position: relative;
}
.msg-continuation-time {
  position: absolute;
  right: calc(100% + 4px);
  top: 2px;
  font-size: 0.6rem;
  opacity: 0;
  white-space: nowrap;
  transition: opacity 0.1s;
}
.msg-meta {
  line-height: 1;
  align-items: baseline !important;
}
.msg-actions {
  opacity: 0;
  transition: opacity 0.1s;
}
.msg-actions-cont {
  position: absolute;
  right: 12px;
  top: 2px;
  display: flex;
  align-items: center;
}
.mention-dropdown {
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  z-index: 200;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px 4px 0 0;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.12);
  max-height: 240px;
  overflow-y: auto;
}
.mention-item-active {
  background: #e8f4fd !important;
}
.msg-tag {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border-radius: 3px;
  padding: 0 2px;
  cursor: pointer;
  font-weight: 500;
  vertical-align: middle;
}
.msg-tag-icon {
  font-size: 11px !important;
  line-height: 1 !important;
}
.msg-tag--channel {
  background: var(--tag-channel-bg);
}
.msg-tag--channel:hover {
  background: var(--tag-channel-bg-hover);
  text-decoration: underline;
}
.msg-tag--mention {
  background: var(--tag-mention-bg);
}
.msg-tag--mention:hover {
  background: var(--tag-mention-bg-hover);
  text-decoration: underline;
}
.msg-tag--deleted {
  color: #999 !important;
  background: #f0f0f0;
  cursor: default;
  text-decoration: line-through;
}
.msg-tag--deleted:hover {
  background: #f0f0f0;
  text-decoration: line-through;
}
.highlight-message {
  animation: msg-highlight 2s ease-out;
}
@keyframes msg-highlight {
  0% {
    background: var(--tag-channel-bg, rgba(25, 118, 210, 0.15));
  }
  100% {
    background: transparent;
  }
}

/* Message attachment images */
.msg-attachment-img {
  max-width: 200px;
  max-height: 180px;
  border-radius: 4px;
  cursor: pointer;
  display: block;
}
.msg-attachment-img--fs {
  max-width: 400px;
  max-height: 300px;
}

/* Channel header name — clickable for members dropdown */
.channel-header-name {
  border-radius: 4px;
  padding: 2px 4px;
  transition: background 0.15s;
}
.channel-header-name:hover {
  background: rgba(0, 0, 0, 0.06);
}

/* Entity link shown in the cohort/analysis channel header */
.channel-entity-tag {
  font-size: 0.85rem;
  flex-shrink: 0;
}
</style>

<style>
/* Lightbox dialog — remove default card/box styling so only the image shows */
.image-lightbox-dialog {
  background: transparent !important;
  box-shadow: none !important;
  overflow: visible;
  width: auto !important;
  max-width: 90vw;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
